import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import contractService from "../../services/contractService";
import LoadingSpinner from "../common/LoadingSpinner";
import CommentForm from "./CommentForm";
import CommentsList from "./CommentsList";
import ContractChangesHistory from "./ContractChangesHistory";
import JewelPhotos from "./JewelPhotos";
import ContractPaymentsModal from "./ContractPaymentsModal";
import { formatDate, formatAmount } from "../../utils/formatters";

const ContractDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser: user, hasPermission } = useAuth();

  // Liste des IDs de la page courante (passée depuis ContractList)
  const contractIds = useMemo(() => {
    return location.state?.contractIds || [];
  }, [location.state?.contractIds]);

  const currentIndex = contractIds.indexOf(id);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex !== -1 && currentIndex < contractIds.length - 1;

  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [paymentsModalOpen, setPaymentsModalOpen] = useState(false); // 🆕 NOUVEAU: État pour la modal des versements
  const [editData, setEditData] = useState({
    discount: 0,
    remark: "",
    etatContrat: "Aucun",
    backgroundColor: "",
  });
  const [updateSuccess, setUpdateSuccess] = useState(false);

  // Formatage du poids
  const formatWeight = (weight) => {
    if (weight === null || weight === undefined) return "-";
    return Number(weight).toFixed(2);
  };

  // Fonction pour calculer l'état du contrat (identique à ContractList)
  // Fonction pour calculer l'état du contrat
  const calculateStatus = useCallback((contract) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Vérifier si le contrat est vendu
    if (contract.remark && contract.remark.toLowerCase().includes("vendu")) {
      return "Contrat vendu";
    }

    if (contract.remark === "R" || contract.remark === "NR") {
      if (contract.closingDate && contract.creationDate) {
        const diffTime = Math.abs(
          new Date(contract.closingDate) - new Date(contract.creationDate)
        );
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return `Rachat sous : ${diffDays} jour(s)`;
      }
    }

    if (contract.limitDate) {
      const limitDate = new Date(contract.limitDate);
      limitDate.setHours(0, 0, 0, 0);
      const diffTime = limitDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays < 0) {
        return "Dépassement d'échéance";
      } else {
        return `Reste : ${diffDays} jour(s) avant terme`;
      }
    }

    return contract.status || "État non défini";
  }, []);

  // 🆕 NOUVEAU: Gestionnaires pour la modal des versements
  const handleOpenPaymentsModal = useCallback(() => {
    setPaymentsModalOpen(true);
  }, []);

  const handleClosePaymentsModal = useCallback(() => {
    setPaymentsModalOpen(false);
  }, []);

  // Fonction pour le retour intelligent
  // 🔧 CORRECTION: Fonction pour le retour intelligent OPTIMISÉE
  const handleBackToList = useCallback(() => {
    const fromContractList = location.state?.fromContractList;

    console.log("🔙 Retour vers liste, fromContractList:", fromContractList);

    if (fromContractList) {
      navigate("/contracts", {
        state: {
          fromContractDetail: true,
          contractId: id,
          timestamp: Date.now(),
        },
      });
    } else {
      navigate("/contracts");
    }
  }, [navigate, location.state, id]);

  // Navigation Précédent / Suivant dans la liste courante
  const handleNavigate = useCallback((targetId) => {
    navigate(`/contracts/${targetId}`, {
      state: {
        fromContractList: location.state?.fromContractList,
        contractIds,
      },
    });
  }, [navigate, location.state, contractIds]);

  const handlePrev = useCallback(() => {
    if (hasPrev) handleNavigate(contractIds[currentIndex - 1]);
  }, [hasPrev, contractIds, currentIndex, handleNavigate]);

  const handleNext = useCallback(() => {
    if (hasNext) handleNavigate(contractIds[currentIndex + 1]);
  }, [hasNext, contractIds, currentIndex, handleNavigate]);


  // Charger les détails du contrat
  useEffect(() => {
    const fetchContractDetails = async () => {
      try {
        setLoading(true);
        const result = await contractService.getContract(id);

        if (result.success) {
          setContract(result.data);
          setEditData({
            discount: result.data.discount || 0,
            remark: result.data.remark || "",
            etatContrat: result.data.etatContrat || "Aucun",
            backgroundColor: result.data.backgroundColor || "",
          });
        } else {
          setError("Erreur lors du chargement des détails du contrat.");
        }
      } catch (error) {
        console.error(
          "Erreur lors du chargement des détails du contrat:",
          error
        );
        setError("Erreur lors du chargement des détails du contrat.");
      } finally {
        setLoading(false);
      }
    };

    fetchContractDetails();
  }, [id]);

  // Gérer le changement des champs lors de l'édition
  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Soumettre les modifications
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const result = await contractService.updateContract(id, editData);

      if (result.success) {
        setContract(result.data);
        setEditMode(false);
        setUpdateSuccess(true);

        setTimeout(() => {
          setUpdateSuccess(false);
        }, 3000);
      } else {
        setError("Erreur lors de la mise à jour du contrat.");
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour du contrat:", error);
      setError("Erreur lors de la mise à jour du contrat.");
    }
  };

  // Gestionnaires pour les photos
  const handlePhotoAdded = async (newPhoto) => {
    console.log("📸 Photo ajoutée, rafraîchissement du contrat...");

    const refreshedContract = await contractService.forceRefreshContract(id);

    if (refreshedContract.success) {
      setContract(refreshedContract.data);
      console.log("✅ Contrat rafraîchi après ajout photo");
    } else {
      setContract((prev) => ({
        ...prev,
        jewelPhotos: [...(prev.jewelPhotos || []), newPhoto],
      }));
    }

    setUpdateSuccess(true);
    setTimeout(() => setUpdateSuccess(false), 3000);
  };

  const handlePhotoDeleted = async (photoId) => {
    console.log("🗑️ Photo supprimée, rafraîchissement du contrat...");

    const refreshedContract = await contractService.forceRefreshContract(id);

    if (refreshedContract.success) {
      setContract(refreshedContract.data);
      console.log("✅ Contrat rafraîchi après suppression photo");
    } else {
      setContract((prev) => ({
        ...prev,
        jewelPhotos: (prev.jewelPhotos || []).filter((p) => p._id !== photoId),
      }));
    }

    setUpdateSuccess(true);
    setTimeout(() => setUpdateSuccess(false), 3000);
  };

  const handlePhotoPrimarySet = async (photoId) => {
    console.log("⭐ Photo principale changée, rafraîchissement du contrat...");

    const refreshedContract = await contractService.forceRefreshContract(id);

    if (refreshedContract.success) {
      setContract(refreshedContract.data);
      console.log("✅ Contrat rafraîchi après changement photo principale");
    } else {
      setContract((prev) => ({
        ...prev,
        jewelPhotos: (prev.jewelPhotos || []).map((p) => ({
          ...p,
          isPrimary: p._id === photoId,
        })),
      }));
    }

    setUpdateSuccess(true);
    setTimeout(() => setUpdateSuccess(false), 3000);
  };

  const handlePhotoDescriptionUpdated = async (photoId, description) => {
    console.log(
      "📝 Description photo mise à jour, rafraîchissement du contrat..."
    );

    const refreshedContract = await contractService.forceRefreshContract(id);

    if (refreshedContract.success) {
      setContract(refreshedContract.data);
      console.log("✅ Contrat rafraîchi après mise à jour description");
    } else {
      setContract((prev) => ({
        ...prev,
        jewelPhotos: (prev.jewelPhotos || []).map((p) =>
          p._id === photoId ? { ...p, description } : p
        ),
      }));
    }

    setUpdateSuccess(true);
    setTimeout(() => setUpdateSuccess(false), 3000);
  };

  // Ajouter un commentaire
  const handleAddComment = async (text) => {
    try {
      const result = await contractService.addComment(id, text);

      if (result.success) {
        const updatedContract = await contractService.getContract(id);
        if (updatedContract.success) {
          setContract(updatedContract.data);
        }

        setUpdateSuccess(true);
        setTimeout(() => {
          setUpdateSuccess(false);
        }, 3000);
      } else {
        setError("Erreur lors de l'ajout du commentaire.");
      }
    } catch (error) {
      console.error("Erreur lors de l'ajout du commentaire:", error);
      setError("Erreur lors de l'ajout du commentaire.");
    }
  };

  // Supprimer un commentaire
  const handleDeleteComment = async (commentId) => {
    try {
      const result = await contractService.deleteComment(id, commentId);

      if (result.success) {
        setContract((prevContract) => ({
          ...prevContract,
          comments: prevContract.comments.filter((c) => c._id !== commentId),
        }));

        setUpdateSuccess(true);
        setTimeout(() => {
          setUpdateSuccess(false);
        }, 3000);
      } else {
        setError(
          "Erreur lors de la suppression du commentaire: " +
            (result.error || "Erreur inconnue")
        );
      }
    } catch (error) {
      console.error("Erreur lors de la suppression du commentaire:", error);
      setError("Erreur lors de la suppression du commentaire.");
    }
  };

  // Répondre à un commentaire
  const handleReplyToComment = (username) => {
    setActiveTab("comments");

    setTimeout(() => {
      const commentTextarea = document.getElementById("comment");
      if (commentTextarea) {
        commentTextarea.value = `@${username} `;
        commentTextarea.focus();
      }
    }, 100);
  };

  if (loading) {
    return <LoadingSpinner message="Chargement des détails du contrat..." />;
  }

  if (error) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-red-100 p-4 rounded-md text-red-700 mb-4">
          {error}
        </div>
        <button onClick={handleBackToList} className="btn btn-secondary">
          Retour à la liste des contrats
        </button>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-yellow-100 p-4 rounded-md text-yellow-700 mb-4">
          Contrat non trouvé.
        </div>
        <button onClick={handleBackToList} className="btn btn-secondary">
          Retour à la liste des contrats
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 🆕 NOUVEAU: Modal des versements */}
      <ContractPaymentsModal
        isOpen={paymentsModalOpen}
        onClose={handleClosePaymentsModal}
        contractNumber={contract?.contractNumber}
      />

      <div className="container mx-auto p-4 max-w-7xl">
        {/* Header moderne */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Contrat {contract.contractNumber}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center">
                  <span className="font-medium">Agence:</span>
                  <span className="ml-1">{contract.agency}</span>
                </span>
                <span className="flex items-center">
                  <span className="font-medium">Client:</span>
                  <span className="ml-1">
                    {contract.client || "Non défini"}
                  </span>
                </span>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    contract.etatContrat === "A vendre"
                      ? "bg-blue-100 text-blue-800"
                      : contract.etatContrat === "Vendu"
                      ? "bg-green-100 text-green-800"
                      : contract.etatContrat === "A surveiller"
                      ? "bg-yellow-100 text-yellow-800"
                      : contract.etatContrat === "Retard"
                      ? "bg-red-100 text-red-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {contract.etatContrat || "Aucun"}
                </span>
              </div>
            </div>
            {/* Bouton retour + navigation Précédent/Suivant */}
            <div className="mt-4 sm:mt-0 flex items-center space-x-2">
              {/* Bouton Versements */}
              <button
                onClick={handleOpenPaymentsModal}
                className="inline-flex items-center px-4 py-2 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                title="Voir les détails des versements"
              >
                <span className="mr-2">💰</span>
                Versements
                {contract?.versements && contract.versements.length > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-blue-100 bg-blue-600 rounded-full">
                    {contract.versements.length}
                  </span>
                )}
              </button>

              {/* Séparateur */}
              {contractIds.length > 0 && <span className="text-gray-300">|</span>}

              {/* Navigation Précédent / Suivant */}
              {contractIds.length > 0 && (
                <>
                  <button
                    onClick={handlePrev}
                    disabled={!hasPrev}
                    title={hasPrev ? "Contrat précédent" : "Premier contrat de la liste"}
                    className={`inline-flex items-center px-3 py-2 border rounded-md shadow-sm text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                      hasPrev
                        ? "border-gray-300 text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
                        : "border-gray-200 text-gray-300 bg-gray-50 cursor-not-allowed"
                    }`}
                  >
                    ‹ Précédent
                  </button>
                  <span className="text-xs text-gray-400 select-none">
                    {currentIndex + 1} / {contractIds.length}
                  </span>
                  <button
                    onClick={handleNext}
                    disabled={!hasNext}
                    title={hasNext ? "Contrat suivant" : "Dernier contrat de la liste"}
                    className={`inline-flex items-center px-3 py-2 border rounded-md shadow-sm text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                      hasNext
                        ? "border-gray-300 text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
                        : "border-gray-200 text-gray-300 bg-gray-50 cursor-not-allowed"
                    }`}
                  >
                    Suivant ›
                  </button>
                  <span className="text-gray-300">|</span>
                </>
              )}

              {/* Bouton retour */}
              <button
                onClick={handleBackToList}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                title={
                  location.state?.fromContractList
                    ? "Retourner à la position précédente dans la liste"
                    : "Retourner à la liste des contrats"
                }
              >
                ← Retour
              </button>
            </div>
          </div>
        </div>

        {updateSuccess && (
          <div className="bg-green-50 border border-green-200 p-4 rounded-md text-green-700 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-green-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium">
                  Modifications enregistrées avec succès
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Debug permissions (dev uniquement) */}
        {process.env.NODE_ENV === "development" && (
          <div className="bg-yellow-100 border border-yellow-200 p-3 rounded-md mb-6 text-xs font-mono">
            <strong className="text-yellow-800">
              🔍 Debug permissions (dev only):
            </strong>
            <br />
            <div className="mt-2 space-y-1">
              <div>
                <strong>User exists:</strong> {user ? "✅ OUI" : "❌ NON"}
              </div>
              <div>
                <strong>Username:</strong> {user?.username || "Non défini"}
              </div>
              <div>
                <strong>Role:</strong> {user?.role || "Non défini"}
              </div>
              <div>
                <strong>Is Admin:</strong>{" "}
                {user?.role === "admin" ? "✅ OUI" : "❌ NON"}
              </div>
              <div>
                <strong>Is Admin Assistant:</strong>{" "}
                {user?.role === "admin_assistant" ? "✅ OUI" : "❌ NON"}
              </div>
              <div>
                <strong>Can Manage Photos:</strong>{" "}
                {user &&
                (user?.role === "admin" || user?.role === "admin_assistant")
                  ? "✅ OUI"
                  : "❌ NON"}
              </div>
              <div>
                <strong>Contract ID:</strong> {id}
              </div>
              <div>
                <strong>Photos count:</strong>{" "}
                {contract?.jewelPhotos?.length || 0}
              </div>
            </div>
          </div>
        )}

        {/* Navigation par onglets moderne */}
        <div className="bg-white shadow-sm rounded-lg mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              {[
                { key: "details", label: "Informations", count: null },
                {
                  key: "photos",
                  label: "Photos",
                  count: contract.jewelPhotos?.length || 0,
                },
                {
                  key: "comments",
                  label: "Commentaires",
                  count: contract.comments?.length || 0,
                },
                { key: "changes", label: "Historique", count: null },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.key
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {tab.label}
                  {tab.count !== null && tab.count > 0 && (
                    <span
                      className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                        activeTab === tab.key
                          ? "bg-blue-100 text-blue-600"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Contenu des onglets */}
          <div className="p-6">
            {/* Onglet Informations */}
            {activeTab === "details" && (
              <div
                className={`grid grid-cols-1 lg:grid-cols-3 gap-6 ${
                  contract.remark &&
                  contract.remark.toLowerCase().includes("vendu")
                    ? "bg-red-300 rounded-lg p-5"
                    : ""
                }`}
              >
                {/* Colonne 1: Informations générales */}
                <div className="bg-gray-50 rounded-lg p-5">
                  <h3 className="text-lg font-semibold mb-4 text-gray-900">
                    Informations générales
                  </h3>
                  <div className="space-y-4">
                    {/* Numéro de contrat */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-blue-700">
                          Numéro de contrat:
                        </span>
                        <span className="text-lg font-bold text-blue-900 font-mono">
                          {contract.contractNumber}
                        </span>
                      </div>
                    </div>

                    {/* Informations client */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-green-700">
                            Client:
                          </span>
                          <span className="text-base font-bold text-green-900">
                            {contract.client || "Non défini"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-green-700">
                            Téléphone:
                          </span>
                          <span className="text-base font-semibold text-green-800 font-mono">
                            {contract.phone1 || "Non défini"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Statut avec formatage conditionnel */}
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-orange-700">
                          Statut:
                        </span>
                        <span className="text-base font-bold text-orange-900">
                          {calculateStatus(contract)}
                        </span>
                      </div>
                    </div>

                    {/* Autres informations standard */}
                    <div className="space-y-3">
                      {[
                        ["Agence", contract.agency],
                        ["Ayant droit", contract.phone2],
                      ].map(([label, value]) => (
                        <div key={label} className="flex justify-between">
                          <span className="font-medium text-gray-600">
                            {label}:
                          </span>
                          <span className="text-gray-900 text-right">
                            {value || "Non défini"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <h4 className="text-md font-semibold mt-6 mb-3 text-gray-900">
                    Détails du contrat
                  </h4>
                  <div className="space-y-3">
                    {[
                      ["Poids bijou", formatWeight(contract.jewelWeight)],
                      ["Poids CDVA", formatWeight(contract.cdvaWeight)],
                      ["Poids CDD", formatWeight(contract.cddWeight)],
                    ].map(([label, value]) => (
                      <div key={label} className="flex justify-between">
                        <span className="font-medium text-gray-600">
                          {label}:
                        </span>
                        <span className="text-gray-900">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Colonne 2: Dates */}
                <div className="bg-gray-50 rounded-lg p-5">
                  <h3 className="text-lg font-semibold mb-4 text-gray-900">
                    Dates
                  </h3>
                  <div className="space-y-3">
                    {[
                      ["Date création", formatDate(contract.creationDate)],
                      ["Date limite", formatDate(contract.limitDate)],
                      [
                        "Dernier versement",
                        formatDate(contract.lastPaymentDate),
                      ],
                      ["Date clôture", formatDate(contract.closingDate)],
                    ].map(([label, value]) => (
                      <div key={label} className="flex justify-between">
                        <span className="font-medium text-gray-600">
                          {label}:
                        </span>
                        <span className="text-gray-900 text-right">
                          {value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Colonne 3: Montants et édition */}
                <div className="bg-gray-50 rounded-lg p-5">
                  <h3 className="text-lg font-semibold mb-4 text-gray-900">
                    Montants
                  </h3>
                  <div className="space-y-4 mb-6">
                    {/* Montant décaissé */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-blue-700">
                          Montant décaissé:
                        </span>
                        <span className="text-lg font-bold text-blue-900 font-mono">
                          {formatAmount(contract.disbursementAmount)}
                        </span>
                      </div>
                    </div>

                    {/* Montant prévu */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-green-700">
                          Montant prévu:
                        </span>
                        <span className="text-lg font-bold text-green-900 font-mono">
                          {formatAmount(contract.expectedAmount)}
                        </span>
                      </div>
                    </div>

                    {/* Montant perçu */}
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-purple-700">
                          Montant perçu:
                        </span>
                        <span className="text-lg font-bold text-purple-900 font-mono">
                          {formatAmount(contract.receivedAmount)}
                        </span>
                      </div>
                    </div>

                    {/* Reste avec couleur dynamique */}
                    <div
                      className={`rounded-lg p-3 border ${
                        (contract.remainingAmount || 0) < 0
                          ? "bg-red-50 border-red-200"
                          : (contract.remainingAmount || 0) === 0
                          ? "bg-green-50 border-green-200"
                          : "bg-yellow-50 border-yellow-200"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span
                          className={`text-sm font-medium ${
                            (contract.remainingAmount || 0) < 0
                              ? "text-red-700"
                              : (contract.remainingAmount || 0) === 0
                              ? "text-green-700"
                              : "text-yellow-700"
                          }`}
                        >
                          Reste:
                        </span>
                        <span
                          className={`text-lg font-bold font-mono ${
                            (contract.remainingAmount || 0) < 0
                              ? "text-red-900"
                              : (contract.remainingAmount || 0) === 0
                              ? "text-green-900"
                              : "text-yellow-900"
                          }`}
                        >
                          {formatAmount(contract.remainingAmount)}
                        </span>
                      </div>
                    </div>

                    {/* Remise */}
                    <div className="flex justify-between py-2 border-t border-gray-200">
                      <span className="font-medium text-gray-600">Remise:</span>
                      <span className="text-gray-900 font-mono">
                        {formatAmount(contract.discount)}
                      </span>
                    </div>
                  </div>

                  {editMode ? (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label
                          htmlFor="discount"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Remise
                        </label>
                        <input
                          type="number"
                          id="discount"
                          name="discount"
                          value={editData.discount}
                          onChange={handleChange}
                          className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="remark"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Remarque
                        </label>
                        <input
                          type="text"
                          id="remark"
                          name="remark"
                          value={editData.remark}
                          onChange={handleChange}
                          className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          maxLength="2"
                          placeholder="Ex: R"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="etatContrat"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          État du contrat
                        </label>
                        <select
                          id="etatContrat"
                          name="etatContrat"
                          value={editData.etatContrat}
                          onChange={handleChange}
                          className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                          <option value="Aucun">Aucun</option>
                          <option value="A vendre">A vendre</option>
                          <option value="Vendu">Vendu</option>
                          <option value="A surveiller">A surveiller</option>
                          <option value="Retard">Retard</option>
                        </select>
                      </div>

                      <div>
                        <label
                          htmlFor="backgroundColor"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Couleur de fond
                        </label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="color"
                            id="backgroundColor"
                            name="backgroundColor"
                            value={editData.backgroundColor}
                            onChange={handleChange}
                            className="h-10 w-16 border-gray-300 rounded-md shadow-sm"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setEditData((prev) => ({
                                ...prev,
                                backgroundColor: "",
                              }))
                            }
                            className="px-3 py-2 text-xs text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                          >
                            Réinitialiser
                          </button>
                        </div>
                      </div>

                      <div className="flex space-x-3 pt-4">
                        <button
                          type="submit"
                          className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                        >
                          Enregistrer
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditMode(false)}
                          className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                        >
                          Annuler
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div>
                      <h4 className="text-md font-semibold mb-3 text-gray-900">
                        Informations additionnelles
                      </h4>
                      <div className="space-y-3 mb-4">
                        <div className="flex justify-between">
                          <span className="font-medium text-gray-600">
                            Remarque:
                          </span>
                          <span className="text-gray-900">
                            {contract.remark || "Aucune"}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-gray-600">
                            Couleur:
                          </span>
                          {contract.backgroundColor ? (
                            <div className="flex items-center">
                              <div
                                className="w-4 h-4 border border-gray-300 rounded mr-2"
                                style={{
                                  backgroundColor: contract.backgroundColor,
                                }}
                              ></div>
                              <span className="text-xs text-gray-500">
                                {contract.backgroundColor}
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-900">Aucune</span>
                          )}
                        </div>
                      </div>

                      {hasPermission("canModify") && (
                        <button
                          onClick={() => setEditMode(true)}
                          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                        >
                          Modifier
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Onglet Photos */}
            {activeTab === "photos" && (
              <JewelPhotos
                contractId={id}
                photos={contract.jewelPhotos || []}
                onPhotoAdded={handlePhotoAdded}
                onPhotoDeleted={handlePhotoDeleted}
                onPhotoPrimarySet={handlePhotoPrimarySet}
                onPhotoDescriptionUpdated={handlePhotoDescriptionUpdated}
              />
            )}

            {/* Onglet Commentaires */}
            {activeTab === "comments" && (
              <div>
                <h3 className="text-lg font-semibold mb-4 text-gray-900">
                  Commentaires
                </h3>

                <div className="mb-6">
                  <CommentsList
                    comments={contract.comments || []}
                    onDelete={
                      hasPermission("canComment") ? handleDeleteComment : null
                    }
                    onReply={
                      hasPermission("canComment") ? handleReplyToComment : null
                    }
                  />
                </div>

                {hasPermission("canComment") && (
                  <div className="pt-4 border-t border-gray-200">
                    <CommentForm
                      onAddComment={handleAddComment}
                      contractId={id}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Onglet Historique */}
            {activeTab === "changes" && (
              <div>
                <h3 className="text-lg font-semibold mb-4 text-gray-900">
                  Historique des modifications
                </h3>
                <ContractChangesHistory
                  contractId={id}
                  contractService={contractService}
                />
              </div>
            )}
          </div>
        </div>

        {/* Footer avec informations sur la version */}
        <div className="bg-white shadow-sm rounded-lg p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm text-gray-500">
            <div>
              <span>Importé le: {formatDate(contract.importedAt, true)}</span>
            </div>
            <div className="mt-2 sm:mt-0 flex items-center space-x-4">
              <span>
                Dernière mise à jour:{" "}
                {formatDate(contract.lastModifiedAt, true)}
              </span>
              <span>Version: {contract.version}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractDetails;
